import { forkJoin, from, iif, Observable, of } from 'rxjs';
import { ofType, StateObservable } from 'redux-observable';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { AxiosError } from 'axios';
import { isUndefined } from 'lodash';
import {
  CreateConfig,
  CreateConfigError,
  CreateConfigSuccess,
  GetActiveConfig,
  GetActiveConfigError,
  GetActiveConfigSuccess,
  JourneyAction,
  JourneyActions, ReplaceConfig,
  ResetConfig, ResetConfigError,
  ResetConfigSuccess,
  UpdateConfig,
  UpdateConfigError,
  UpdateConfigSuccess,
  UploadLogo,
  UploadLogoError, UploadVehicleImage,
} from '../actions/journey';
import JourneyService from '../services/journey.service';
import { CreateErrorNotice, CreateSuccessNotice } from '../../store/actions/toaster.action';

import DefaultConfig from '../assets/journeyconfig.json';
import { RootState } from '../../store/state';
import { GetActiveConfigResponse, JourneyConfig } from '../../shared/models/journey';
import {
  JOURNEY_VEHICLE_LOGO_IMAGE,
  JOURNEY_VEHICLE_VAN_IMAGE,
  JOURNEY_VEHICLE_TYPE
} from '../constants';

const getActiveConfigEpic = (action$: Observable<JourneyAction>) => action$.pipe(
  ofType<GetActiveConfig>(JourneyActions.GetActiveConfig),
  switchMap(({ orgID }) => from(JourneyService.getActiveConfig(orgID)).pipe(
    mergeMap((payload: GetActiveConfigResponse) => iif(
      () => payload.id !== null,
      of(new GetActiveConfigSuccess(payload.id, payload.config, payload.previous)),
      of(new CreateConfig(orgID, DefaultConfig as unknown as JourneyConfig)),
    )),
    catchError((error: AxiosError) => of(
      new GetActiveConfigError(error.message),
      new CreateErrorNotice('Error', 'Failed to load configuration.'),
    )),
  )),
);

const createConfigEpic = (action$: Observable<JourneyAction>) => action$.pipe(
  ofType<CreateConfig>(JourneyActions.CreateConfig),
  switchMap(({ orgID, config }) => from(JourneyService.createConfig(orgID, config)).pipe(
    mergeMap((configID: string) => of(
      new CreateConfigSuccess(configID),
      new GetActiveConfig(orgID),
    )),
    catchError(error => of(
      new CreateConfigError(error.message),
      new CreateErrorNotice('Error', 'Failed to create configuration.'),
    )),
  )),
);

const uploadLogoEpic = (action$: Observable<JourneyAction>) => action$.pipe(
  ofType<UploadLogo>(JourneyActions.UploadLogo),
  switchMap(({ orgID, logo, path, key }) => from(JourneyService.uploadImage(orgID, logo, key)).pipe(
    mergeMap((payload: string) => of(
      new UpdateConfig(orgID, [
        {
          path,
          value: payload,
        },
      ]),
      new CreateSuccessNotice('Success', 'Logo updated successfully!'),
    )),
    catchError(error => of(
      new UploadLogoError(error.message),
      new CreateErrorNotice('Error', 'Failed to upload logo.'),
    )),
  )),
);

const uploadVehicleImageEpic = (action$: Observable<JourneyAction>) => action$.pipe(
  ofType<UploadVehicleImage>(JourneyActions.UploadVehicleImage),
  switchMap(({ orgID, vehicleImage, logoFile, logoUrl, vehicleType }) => {
    const actions = {} as any;
    if (vehicleImage) {
      actions.image = JourneyService.uploadImage(orgID, vehicleImage, 'image');
    }
    if (logoFile) {
      actions.logo = JourneyService.uploadImage(orgID, logoFile, 'image');
    } else {
      actions.logo = Promise.resolve(logoUrl);
    }
    if (vehicleType) {
      actions.type = Promise.resolve(vehicleType);
    }
    return forkJoin(
      actions
    ).pipe(
      mergeMap((data: any) => {
        const updates = [];
        if (data.image) {
          updates.push({
            path: JOURNEY_VEHICLE_VAN_IMAGE,
            value: data.image,
          });
        }
        if (!isUndefined(data.logo)) {
          updates.push({
            path: JOURNEY_VEHICLE_LOGO_IMAGE,
            value: data.logo,
          });
        }
        if (data.type) {
          updates.push({
            path: JOURNEY_VEHICLE_TYPE,
            value: data.type,
          });
        }
        return of(
          new UpdateConfig(orgID, updates),
          new CreateSuccessNotice('Success', 'Logo updated successfully!'),
        )
      }),
      catchError(error => of(
        new UploadLogoError(error.message),
        new CreateErrorNotice('Error', 'Failed to upload logo.'),
      )),
    )
  }),
);


const updateConfigEpic = (action$: Observable<JourneyAction>, state$: StateObservable<RootState>) => action$.pipe(
  ofType<UpdateConfig>(JourneyActions.UpdateConfig),
  switchMap(({ orgID, updates }) => from(JourneyService.updateConfig(orgID, state$.value.journey.config.configID, state$.value.journey.config.data, updates)).pipe(
    mergeMap(() => of(
      new UpdateConfigSuccess(),
      new GetActiveConfig(orgID),
      new CreateSuccessNotice('Success', 'Configuration was updated successfully!'),
    )),
    catchError(error => of(
      new UpdateConfigError(error.message),
      new CreateErrorNotice('Error', 'Failed to update configuration'),
    )),
  )),
);

const replaceConfigEpic = (action$: Observable<JourneyAction>, state$: StateObservable<RootState>) => action$.pipe(
  ofType<ReplaceConfig>(JourneyActions.ReplaceConfig),
  switchMap(({ orgID, config }) => from(JourneyService.updateConfig(orgID, state$.value.journey.config.configID, config, [])).pipe(
    mergeMap(() => of(
      new ResetConfigSuccess(),
      new GetActiveConfig(orgID),
      new CreateSuccessNotice('Success', 'Configuration was updated successfully!'),
    )),
    catchError(error => of(
      new ResetConfigError(error.message),
      new CreateErrorNotice('Error', 'Failed to update configuration'),
    )),
  )),
);

const resetConfigEpic = (action$: Observable<JourneyAction>, state$: StateObservable<RootState>) => action$.pipe(
  ofType<ResetConfig>(JourneyActions.ResetConfig),
  switchMap(({ orgID }) => from(JourneyService.updateConfig(orgID, state$.value.journey.config.configID, DefaultConfig as unknown as JourneyConfig, null)).pipe(
    mergeMap(() => of(
      new ResetConfigSuccess(),
      new CreateSuccessNotice('Success', 'Configuration was reset.'),
      new GetActiveConfig(orgID),
    )),
    catchError(error => of(
      new ResetConfigError(error.message),
      new CreateErrorNotice('Error', 'Failed to reset configuration'),
    )),
  )),
);

const JourneyEpics = [
  getActiveConfigEpic,
  createConfigEpic,
  uploadLogoEpic,
  updateConfigEpic,
  resetConfigEpic,
  replaceConfigEpic,
  uploadVehicleImageEpic
];

export default JourneyEpics;
